// @flow

// Libs
import _map from 'lodash/map'

// Effect Manager
import { take, fork, put, select, call, race } from 'redux-saga/effects'

// Operator
import reopenJobOperator from 'hfty-operator-reopen-job'

// Constants
import { REOPEN_JOB } from 'hfty-reopen-job-dom'
import { SAMPLING } from 'fc-model-sampling-jobs'

// Selectors
import { jobs$ } from 'hfty-soil-jobs'
import { firstAvailableGrowerId$ } from 'fc-assets'
import reopenJobOperations$ from 'hfty-soil-jobs-dom/src/selectors/reopenJobOperations'

// Controller
import controller from 'fc-ctrl-sampling-jobs'

// Types
import type { Job } from 'fc-model-sampling-jobs/types'
import { ReopenJobOperation } from 'hfty-operator-reopen-job/types'

export default function *watchReopenJobOperations (): Generator<*, void, *> {
  while (true) {
    const { job } = yield take(REOPEN_JOB)

    yield fork(handleReopenJob, job)
    yield fork(handleOperations)
  }
}

export function *handleReopenJob (job: Job): Generator<*, void, *> {
  const jobs: Array<Job> = yield select(jobs$)
  let jobToReopen = jobs.find((jobObject: Job): boolean => jobObject.id === job.id)
  const operation = reopenJobOperator.create({
    model: {
      job: jobToReopen,
    },
  })

  yield put(reopenJobOperator.actionCreators.add(operation))
}

export function *handleOperations (): Generator<*, void, *> {
  let done = false

  while (!done) {
    const effect = yield race({
      submit: take(reopenJobOperator.actionTypes.submitOperations()),
      cancel: take(reopenJobOperator.actionTypes.cancelOperations()),
    })

    if (effect.submit) {
      done = yield call(handleSubmitOperations)
    } else {
      done = true
    }
  }
}

export function *handleSubmitOperations (): Generator<*, boolean, *> {
  const operations: Object = yield select(reopenJobOperations$)
  const nextOperations: Array<ReopenJobOperation> =
    yield _map(operations, function (operation: ReopenJobOperation): Object {
      return call(handleSubmitOperation, operation)
    })

  return nextOperations.every((operation: ReopenJobOperation): boolean => operation.success)
}

export function *handleSubmitOperation (operation: ReopenJobOperation): Generator<*, ReopenJobOperation, *> {
  const growerId = yield select(firstAvailableGrowerId$)
  const updatedOperation = {
    ...operation,
    model: {
      job: {
        ...operation.model.job,
        status: SAMPLING,
      },
    },
  }
  const { error, response } = yield call([controller, controller.update], {
    model: {...updatedOperation.model.job},
    queryParams: {
      rootAsset: growerId,
    },
  })

  if (error) {
    const nextOperation = {
      ...updatedOperation,
      submitting: false,
      success: false,
      errors: response,
    }

    yield put(reopenJobOperator.actionCreators.updateOperation(nextOperation))
    return nextOperation
  } else {
    const nextOperation = {
      ...updatedOperation,
      submitting: false,
      success: true,
    }

    yield put(reopenJobOperator.actionCreators.cancelOperation(nextOperation))
    return nextOperation
  }
}
